import type { ProjectionResult } from '@/domain/engine';
import type { AcquisitionModel, SimulatorState } from '@/domain/types';

const MODEL_LABELS: Record<AcquisitionModel, string> = {
  's-curve': 'montée lente (courbe en S)',
  'linear-ramp': 'rampe linéaire',
  constant: 'constant',
};

/**
 * Formatage numérique **destiné à une lecture machine** (analyse par un LLM) :
 * point décimal, pas de séparateur de milliers, nombre de décimales fixe.
 * Volontairement distinct de `@/lib/format` (fr-FR, espaces insécables) qui vise
 * l'affichage humain et casserait le parsing.
 */
function num(value: number, decimals = 0): string {
  if (!Number.isFinite(value)) return '—';
  return value.toFixed(decimals);
}

function breakEvenLabel(month: number | null, horizonMonths: number): string {
  return month === null ? `non atteint sur ${horizonMonths} mois` : `mois ${month}`;
}

function mdRow(cells: (string | number)[]): string {
  return `| ${cells.join(' | ')} |`;
}

function mdTable(headers: string[], rows: (string | number)[][]): string {
  const separator = headers.map(() => '---');
  return [mdRow(headers), mdRow(separator), ...rows.map(mdRow)].join('\n');
}

/**
 * Construit un rapport Markdown auto-suffisant du scénario et de sa projection,
 * pensé pour être copié/collé dans un contexte d'analyse LLM : synthèse textuelle,
 * tables de paramètres, puis tables reproduisant les données des quatre diagrammes
 * (MRR, coûts vs revenus, clients par palier, cash flow cumulé).
 *
 * Fonction **pure** : ne dépend d'aucun état React et ne déclenche aucun effet.
 */
export function buildMarkdownExport(
  state: SimulatorState,
  projection: ProjectionResult,
  scenarioName = state.info?.title?.trim() || 'Mon scénario',
): string {
  const { team, acquisition, tiers, recurringCosts, fixedCosts, categories } = state;
  const horizonMonths = state.projection.horizonMonths;
  const { snapshots, operationalBreakEvenMonth, globalBreakEvenMonth, finalKpis } = projection;

  const categoryName = (id: string): string => categories.find((c) => c.id === id)?.name ?? '—';

  const chargesMultiplier = team.chargesMultiplier ?? 1;
  const teamMonthlyCost = (headcount: number) =>
    (headcount * team.avgAnnualCostPerFTE * chargesMultiplier) / 12;
  const recurringMonthlyTotal = recurringCosts.reduce((s, c) => s + c.monthlyAmount, 0);
  const monthlyCostInitial = teamMonthlyCost(team.headcount) + recurringMonthlyTotal;
  const targetHeadcount = team.headcountRamp?.targetHeadcount ?? team.headcount;
  const monthlyCostCruise = teamMonthlyCost(targetHeadcount) + recurringMonthlyTotal;
  const rampEnabled = team.headcountRamp?.enabled === true;
  const totalFixed = fixedCosts.reduce((s, c) => s + c.amount, 0);

  const model = acquisition.model ?? 'constant';
  const rampUpMonths = acquisition.rampUpMonths ?? 0;
  const rate = acquisition.newClientsPerMonth;
  const hasEntry = tiers.some((t) => (t.entryPrice ?? 0) > 0 || (t.entryCost ?? 0) > 0);
  const hasSupportCost = tiers.some((t) => (t.costPerClientPerMonth ?? 0) > 0);
  const discrete = acquisition.discrete === true;
  const salesCycleMonths = acquisition.salesCycleMonths ?? 0;
  const winRate = acquisition.winRate ?? 1;
  const presalesCostPerDeal = acquisition.presalesCostPerDeal ?? 0;
  const clientDecimals = discrete ? 0 : 1;
  // Un rythme fractionnaire (ex. 0,5) doit rester visible ; un rythme entier reste « 10 ».
  const rateDecimals = Number.isInteger(rate) ? 0 : 2;

  const baseAcquisitionSentence =
    model === 'constant'
      ? `un modèle constant de ${num(rate, rateDecimals)} nouveau(x) client(s) par mois dès le premier mois`
      : `un modèle « ${MODEL_LABELS[model]} » atteignant un rythme de croisière de ${num(
          rate,
          rateDecimals,
        )} nouveau(x) client(s) par mois après une montée en charge de ${num(rampUpMonths)} mois`;
  const acquisitionSentence = discrete
    ? `${baseAcquisitionSentence}, en deals entiers (mode événementiel B2G) avec un cycle de vente de ${num(
        salesCycleMonths,
      )} mois entre signature et première facturation et un taux de win de ${num(winRate * 100)} %`
    : baseAcquisitionSentence;

  const costSentence = rampEnabled
    ? `un coût mensuel récurrent qui évolue de ${num(monthlyCostInitial)} € à ${num(monthlyCostCruise)} €`
    : `un coût mensuel récurrent de ${num(monthlyCostInitial)} €`;

  const lines: string[] = [];

  lines.push(`# Simulation SaaS — ${scenarioName}`);
  lines.push('');
  const description = state.info?.description?.trim();
  if (description) {
    lines.push(`> ${description.replace(/\n/g, '\n> ')}`);
    lines.push('');
  }
  lines.push(
    `Export généré le ${new Date().toISOString()} · Devise : EUR · Projection sur ${horizonMonths} mois.`,
  );
  lines.push('');

  // --- Synthèse textuelle ---
  lines.push('## Synthèse');
  lines.push('');
  lines.push(
    `Projection sur **${horizonMonths} mois**. L'acquisition suit ${acquisitionSentence}. ` +
      `L'équipe compte ${num(team.headcount, 2)} ETP` +
      (rampEnabled ? ` (montée vers ${num(targetHeadcount, 2)} ETP)` : '') +
      `, pour ${costSentence}` +
      (totalFixed > 0
        ? `, auxquels s'ajoutent ${num(totalFixed)} € de coûts fixes ponctuels`
        : '') +
      ((state.initialDeficit ?? 0) > 0
        ? `. Déficit initial de trésorerie : ${num(state.initialDeficit ?? 0)} €`
        : '') +
      '.',
  );
  lines.push('');

  // --- Indicateurs clés ---
  lines.push("## Indicateurs clés (fin d'horizon)");
  lines.push('');
  lines.push(
    mdTable(
      ['Indicateur', 'Valeur'],
      [
        [`MRR au mois ${horizonMonths}`, `${num(finalKpis.mrr)} €`],
        [`ARR au mois ${horizonMonths}`, `${num(finalKpis.arr)} €`],
        [`Clients actifs au mois ${horizonMonths}`, num(finalKpis.totalClients, clientDecimals)],
        [
          'Break-even opérationnel (MRR ≥ coût récurrent)',
          breakEvenLabel(operationalBreakEvenMonth, horizonMonths),
        ],
        [
          'Break-even global (cash flow cumulé ≥ 0)',
          breakEvenLabel(globalBreakEvenMonth, horizonMonths),
        ],
      ],
    ),
  );
  lines.push('');

  // --- Paramètres : équipe ---
  lines.push('## Paramètres');
  lines.push('');
  lines.push('### Équipe & coûts');
  lines.push('');
  const teamRows: (string | number)[][] = [['Effectif initial (ETP)', num(team.headcount, 2)]];
  if (rampEnabled) teamRows.push(['Effectif cible / plateau (ETP)', num(targetHeadcount, 2)]);
  teamRows.push(
    ['Coût moyen / ETP / an (€)', num(team.avgAnnualCostPerFTE)],
    ['Multiplicateur de charges', num(chargesMultiplier, 2)],
    [
      'Coût mensuel récurrent (€)',
      rampEnabled
        ? `${num(monthlyCostInitial)} → ${num(monthlyCostCruise)}`
        : num(monthlyCostInitial),
    ],
    ['Coûts fixes ponctuels — total (€)', num(totalFixed)],
  );
  if ((state.initialDeficit ?? 0) > 0)
    teamRows.push(['Déficit initial (€)', num(state.initialDeficit ?? 0)]);
  lines.push(mdTable(['Paramètre', 'Valeur'], teamRows));
  lines.push('');

  // --- Paramètres : acquisition ---
  lines.push('### Acquisition & projection');
  lines.push('');
  const acqRows: (string | number)[][] = [
    ['Modèle', MODEL_LABELS[model]],
    [
      model === 'constant' ? 'Rythme (clients/mois)' : 'Rythme de croisière (clients/mois)',
      num(rate, rateDecimals),
    ],
  ];
  if (model !== 'constant') acqRows.push(['Montée en charge (mois)', num(rampUpMonths)]);
  if (discrete) {
    acqRows.push(
      ['Mode', 'deals entiers (événementiel B2G)'],
      ['Cycle de vente signature → facturation (mois)', num(salesCycleMonths)],
      ['Taux de win des dossiers', `${num(winRate * 100)} %`],
      ['Coût avant-vente / dossier (€)', num(presalesCostPerDeal)],
    );
  }
  acqRows.push(['Horizon (mois)', num(horizonMonths)]);
  lines.push(mdTable(['Paramètre', 'Valeur'], acqRows));
  lines.push('');

  // --- Paramètres : paliers ---
  if (tiers.length > 0) {
    lines.push('### Paliers tarifaires');
    lines.push('');
    const headers = ['Palier', 'Prix/mois (€)'];
    if (hasEntry) headers.push("Prix d'entrée (€)", 'Coût unitaire entrée (€)');
    if (hasSupportCost) headers.push('Coût support/client (€/mois)');
    headers.push('Churn mensuel', "Part d'acquisition");
    const rows = tiers.map((t) => {
      const cells: (string | number)[] = [t.name, num(t.monthlyPrice)];
      if (hasEntry) cells.push(num(t.entryPrice ?? 0), num(t.entryCost ?? 0));
      if (hasSupportCost) cells.push(num(t.costPerClientPerMonth ?? 0));
      cells.push(`${num(t.monthlyChurnRate * 100, 1)} %`, `${num(t.acquisitionShare * 100)} %`);
      return cells;
    });
    lines.push(mdTable(headers, rows));
    lines.push('');
  }

  // --- Paramètres : coûts récurrents ---
  if (recurringCosts.length > 0) {
    lines.push('### Coûts récurrents (mensuels)');
    lines.push('');
    const rows: (string | number)[][] = recurringCosts.map((c) => [
      c.label,
      categoryName(c.categoryId),
      num(c.monthlyAmount),
    ]);
    rows.push(['**Total**', '', `**${num(recurringMonthlyTotal)}**`]);
    lines.push(mdTable(['Poste', 'Catégorie', 'Montant/mois (€)'], rows));
    lines.push('');
  }

  // --- Paramètres : coûts fixes ---
  if (fixedCosts.length > 0) {
    lines.push('### Coûts fixes ponctuels');
    lines.push('');
    const rows: (string | number)[][] = fixedCosts.map((c) => [
      c.label,
      categoryName(c.categoryId),
      num(c.occursAtMonth),
      num(c.amount),
    ]);
    rows.push(['**Total**', '', '', `**${num(totalFixed)}**`]);
    lines.push(mdTable(['Poste', 'Catégorie', 'Mois', 'Montant (€)'], rows));
    lines.push('');
  }

  // --- Données mensuelles : projection financière (MRR, coûts, revenus, cash flow) ---
  lines.push('## Données mensuelles');
  lines.push('');
  lines.push('### Projection financière');
  lines.push('');
  lines.push(
    '_Reprend les diagrammes « MRR », « Coûts vs Revenus » et « Cash flow cumulé ». ' +
      "Revenu total = MRR + marge nette sur frais d'entrée._",
  );
  lines.push('');
  const finHeaders = ['Mois', 'Clients'];
  if (discrete) finHeaders.push('Signatures');
  finHeaders.push('MRR (€)', 'ARR (€)', 'Coût mensuel (€)');
  if (discrete) finHeaders.push('Avant-vente (€)');
  if (hasSupportCost) finHeaders.push('Coût support (€)');
  finHeaders.push(
    'Effectif (ETP)',
    'Revenu entrée net (€)',
    'Revenu total (€)',
    'Marge (€)',
    'Revenu cumulé (€)',
    'Coût cumulé (€)',
    'Cash flow cumulé (€)',
  );
  const finRows = snapshots.map((s) => {
    const entryNet = s.oneTimeRevenueThisMonth - s.oneTimeCostThisMonth;
    const cells: (string | number)[] = [s.month, num(s.totalClients, clientDecimals)];
    if (discrete) cells.push(num(s.signaturesThisMonth));
    cells.push(num(s.mrr), num(s.arr), num(s.monthlyCost));
    if (discrete) cells.push(num(s.presalesCostThisMonth));
    if (hasSupportCost) cells.push(num(s.variableCostThisMonth));
    cells.push(
      num(s.headcountThisMonth, 2),
      num(entryNet),
      num(s.mrr + entryNet),
      num(s.margin),
      num(s.cumulativeRevenue),
      num(s.cumulativeCost),
      num(s.cashFlow),
    );
    return cells;
  });
  lines.push(mdTable(finHeaders, finRows));
  lines.push('');

  // --- Données mensuelles : clients par palier ---
  if (tiers.length > 0) {
    lines.push('### Clients actifs par palier');
    lines.push('');
    lines.push('_Reprend le diagramme « Clients par palier »._');
    lines.push('');
    const cbtHeaders = ['Mois', ...tiers.map((t) => `${t.name} (clients)`), 'Total'];
    const cbtRows = snapshots.map((s) => [
      s.month,
      ...tiers.map((t) => num(s.clientsPerTier[t.id] ?? 0, clientDecimals)),
      num(s.totalClients, clientDecimals),
    ]);
    lines.push(mdTable(cbtHeaders, cbtRows));
    lines.push('');
  }

  return lines.join('\n');
}

/**
 * Déclenche le téléchargement d'un fichier `.md` construit par `buildMarkdownExport`.
 */
export function exportToMarkdownFile(state: SimulatorState, projection: ProjectionResult): void {
  const markdown = buildMarkdownExport(state, projection);
  const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `saas-sim-${new Date().toISOString().slice(0, 10)}.md`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
